import BigNumber from 'bignumber.js';

import StarknetWallet from '#/features/starknet-wallet/wallet';
import L1_ERC20_BRIDGE_ABI from '#/features/abis/L1_ERC20_BRIDGE_ABI';
import { BridgedToken, type ParaclearContract } from '#/features/paraclear';
import { type StarknetProvider } from '#/features/starknet-wallet/provider';
import EthereumWallet, {
  EthereumAddress,
} from '#/features/ethereum-wallet/wallet';

import { toQuantums, fromQuantums } from '#/utils/quantums';
import toIntString from '#/utils/toIntString';

/**
 * Issues a batched transaction to withdraw funds from Paraclear
 * and initiate withdraw on the ERC20 Token bridge on StarkNet.
 *
 * Only waits for the transaction to be sent to StarkNet.
 *
 * @param amount Amount to be withdrawn.
 * @param token Token to be withdrawn.
 * @param l1Recipient Address of the recipient account in Ethereum.
 *
 * @returns Transaction hash
 */
export async function requestInitiateWithdraw(
  amount: BigNumber,
  l1Recipient: string,
  token: BridgedToken,
  paraclear: ParaclearContract,
  provider: StarknetProvider,
): Promise<string> {
  const tokenAddress = toIntString(token.l2TokenAddress);

  const paraclearAmount = toQuantums(amount, paraclear.decimals);
  const paraclearTransaction = {
    contractAddress: paraclear.address,
    entrypoint: 'withdraw',
    calldata: [tokenAddress, paraclearAmount],
  };

  const res = await StarknetWallet.callContract(provider, {
    contractAddress: paraclear.address,
    entrypoint: 'getSocializedLossFactor',
  });
  const socializedLossFactor = fromQuantums(
    toIntString(res.result[0] ?? 0),
    paraclear.decimals,
  );
  const receivableAmount = amount.times(
    BigNumber(1).minus(socializedLossFactor),
  );
  const bridgeAmount = toQuantums(receivableAmount, token.decimals);

  const bridgeTransaction = {
    contractAddress: token.l2BridgeAddress,
    entrypoint: 'initiate_withdraw',
    calldata: [toIntString(l1Recipient), bridgeAmount, '0'],
  };

  const transactionBatch = [paraclearTransaction, bridgeTransaction];
  const response = await StarknetWallet.executeTransaction(
    provider,
    transactionBatch,
  );
  return response.transaction_hash;
}

/**
 * Issues a request to sign a transaction to withdraw
 * funds from Ethereum ERC20 Token Bridge.
 *
 * @param amount Amount to be withdrawn. Must be the same requested in StarkNet.
 * @param l1Recipient Address of the recipient account in Ethereum.
 * @param token Token to be collected.
 *
 * @returns An object containing the transaction hash.
 */
export async function requestCollectFunds(
  amount: BigNumber,
  l1Recipient: EthereumAddress,
  token: BridgedToken,
): Promise<{ hash: string }> {
  const amountQuantums = toQuantums(amount, token.decimals);

  const { request } = await EthereumWallet.prepareWriteContract({
    address: token.l1BridgeAddress,
    abi: L1_ERC20_BRIDGE_ABI,
    functionName: 'withdraw',
    args: [BigInt(amountQuantums), l1Recipient],
    /* the account to send the transaction from. */
    account: l1Recipient,
  });

  return EthereumWallet.writeContract(request);
}
